import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Paper,
  alpha,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Inventory,
  Description,
  Add,
  ArrowForward,
  MoreVert,
  TrendingDown,
  CheckCircle,
  Schedule,
} from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { dashboardAPI } from '../services/api';
import { toast } from 'react-toastify';

const statusColors = {
  draft: 'default',
  sent: 'primary',
  viewed: 'info',
  accepted: 'success',
  rejected: 'error',
  expired: 'warning',
  converted: 'secondary',
};

const COLORS = ['#875A7B', '#00A09D', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1'];

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#875A7B' }} />
      </Box>
    );
  }

  const summaryCards = [
    {
      title: 'Total Quotations',
      value: stats?.summary?.totalQuotations || 0,
      icon: <Description sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #875A7B 0%, #714B68 100%)',
      shadowColor: 'rgba(135, 90, 123, 0.4)',
      change: '+12%',
      changeType: 'up',
    },
    {
      title: 'Active Quotations',
      value: stats?.summary?.activeQuotations || 0,
      icon: <Schedule sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      shadowColor: 'rgba(16, 185, 129, 0.4)',
      change: '+8%',
      changeType: 'up',
    },
    {
      title: 'Total Customers',
      value: stats?.summary?.totalCustomers || 0,
      icon: <People sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      shadowColor: 'rgba(245, 158, 11, 0.4)',
      change: '+5%',
      changeType: 'up',
    },
    {
      title: 'Total Products',
      value: stats?.summary?.totalProducts || 0,
      icon: <Inventory sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      shadowColor: 'rgba(236, 72, 153, 0.4)',
      change: '+3%',
      changeType: 'up',
    },
  ];

  const pieData = stats?.quotationsByStatus
    ? Object.entries(stats.quotationsByStatus).map(([name, data]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: data.count,
      }))
    : [];

  const monthlyData = stats?.monthlyTrend?.map((item) => ({
    month: `${item._id.month}/${item._id.year}`,
    quotations: item.count,
    value: item.totalValue / 1000,
  })) || [];

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'flex-start' }, 
        gap: 2,
        mb: 4 
      }}>
        <Box>
          <Typography variant="h4" fontWeight="700" sx={{ color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Welcome back! Here's what's happening with your business.
          </Typography>
        </Box>
        {/* <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/quotations/new')}
          sx={{
            backgroundColor: '#875A7B',
            boxShadow: '0 4px 12px rgba(135, 90, 123, 0.3)',
            px: 3,
            py: 1.2,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#714B68',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(135, 90, 123, 0.4)',
            },
          }}
        >
          New Quotation
        </Button> */}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                background: '#fff',
                borderRadius: '20px',
                overflow: 'visible',
                position: 'relative',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '16px',
                      background: card.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      boxShadow: `0 8px 24px ${card.shadowColor}`,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Chip
                    size="small"
                    icon={card.changeType === 'up' ? <TrendingUp sx={{ fontSize: 16 }} /> : <TrendingDown sx={{ fontSize: 16 }} />}
                    label={card.change}
                    sx={{
                      bgcolor: card.changeType === 'up' ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1),
                      color: card.changeType === 'up' ? '#10b981' : '#ef4444',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      '& .MuiChip-icon': {
                        color: 'inherit',
                      },
                    }}
                  />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                  {card.value.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* This Month Stats & Pie Chart */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%', borderRadius: '20px' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="700" color="#1e293b">
                  This Month's Performance
                </Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Box
                    sx={{
                      p: { xs: 1.5, md: 2.5 },
                      borderRadius: '16px',
                      bgcolor: alpha('#875A7B', 0.08),
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h4" fontWeight="700" sx={{ color: '#875A7B', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {stats?.thisMonth?.count || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Quotations
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: '16px',
                      bgcolor: alpha('#10b981', 0.08),
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h4" fontWeight="700" sx={{ color: '#10b981' }}>
                      {stats?.thisMonth?.accepted || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Won
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: '16px',
                      bgcolor: alpha('#f59e0b', 0.08),
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h4" fontWeight="700" sx={{ color: '#f59e0b' }}>
                      {stats?.thisMonth?.conversionRate || stats?.summary?.conversionRate || 0}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Conversion
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: '16px',
                      bgcolor: alpha('#ec4899', 0.08),
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h5" fontWeight="700" sx={{ color: '#ec4899' }}>
                      {formatCurrency(stats?.thisMonth?.totalValue).replace('₹', '')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Total Value
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%', borderRadius: '20px' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="700" color="#1e293b" sx={{ mb: 2 }}>
                Quotations by Status
              </Typography>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {pieData.map((item, index) => (
                  <Chip
                    key={item.name}
                    size="small"
                    label={`${item.name}: ${item.value}`}
                    sx={{
                      bgcolor: alpha(COLORS[index % COLORS.length], 0.1),
                      color: COLORS[index % COLORS.length],
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Trend Chart */}
      <Card sx={{ mb: 4, borderRadius: '20px' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" fontWeight="700" color="#1e293b">
                Revenue Trend
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last 6 months performance
              </Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#875A7B' }} />
                <Typography variant="caption" color="text.secondary">Quotations</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10b981' }} />
                <Typography variant="caption" color="text.secondary">Value (K)</Typography>
              </Box>
            </Box>
          </Box>
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="colorQuotations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#875A7B" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#875A7B" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                <YAxis yAxisId="left" orientation="left" stroke="#94a3b8" fontSize={12} />
                <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={12} />
                <RechartsTooltip
                  contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                  }}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="quotations"
                  stroke="#875A7B"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorQuotations)"
                  name="Quotations"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="value"
                  stroke="#10b981"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorValue)"
                  name="Value (K)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <Typography color="text.secondary">No data available</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Recent Quotations */}
      <Card sx={{ borderRadius: '20px' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="700" color="#1e293b">
              Recent Quotations
            </Typography>
            <Button
              endIcon={<ArrowForward />}
              onClick={() => navigate('/quotations')}
              sx={{
                color: '#875A7B',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: alpha('#875A7B', 0.08),
                },
              }}
            >
              View All
            </Button>
          </Box>
          <TableContainer
            sx={{
              borderRadius: '12px',
              border: '1px solid #f1f5f9',
              '& .MuiTableCell-head': {
                bgcolor: '#f8fafc',
                fontWeight: 600,
              },
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Quotation #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats?.recentQuotations?.length > 0 ? (
                  stats.recentQuotations.map((quotation) => (
                    <TableRow
                      key={quotation._id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha('#875A7B', 0.04),
                        },
                      }}
                      onClick={() => navigate(`/quotations/${quotation._id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#875A7B' }}>
                          {quotation.quotationNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '10px',
                              backgroundColor: '#875A7B',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                            }}
                          >
                            {(quotation.customer?.name || quotation.customerSnapshot?.name || 'N')?.charAt(0)}
                          </Box>
                          <Typography variant="body2" fontWeight={500}>
                            {quotation.customer?.name || quotation.customerSnapshot?.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{quotation.title}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(quotation.grandTotal)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={quotation.status}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            bgcolor: alpha(
                              quotation.status === 'accepted' ? '#10b981' :
                              quotation.status === 'rejected' ? '#ef4444' :
                              quotation.status === 'sent' ? '#875A7B' :
                              quotation.status === 'draft' ? '#64748b' : '#f59e0b',
                              0.1
                            ),
                            color:
                              quotation.status === 'accepted' ? '#10b981' :
                              quotation.status === 'rejected' ? '#ef4444' :
                              quotation.status === 'sent' ? '#875A7B' :
                              quotation.status === 'draft' ? '#64748b' : '#f59e0b',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(quotation.quotationDate).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Description sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                          No quotations yet
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => navigate('/quotations/new')}
                          sx={{
                            backgroundColor: '#875A7B',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#714B68' },
                          }}
                        >
                          Create your first quotation
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
